import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { TransactionTrendsResponse } from '@/store/transactionsApi';
import { formatChartDateAxis } from '@/utils/charts/axisFormatters';
import {
  formatNumberToCurrency,
  getCurrencyStringTitleForChart,
} from '@/utils/currency';
import { useGraphColors } from '@/hooks/useGraphColors';
import { useLocalization } from '@/hooks/useLocalization';
import { CashFlowTabMode } from './CashFlowDetailsBarChart';
import { buildTrendBarChartData } from './cashFlowTrendBarChartData';

const BAR_CHART_HEIGHT = 340;
interface CashFlowTrendBarChartProps {
  transactionTrendsData?: TransactionTrendsResponse;
  mode: CashFlowTabMode;
}

const CashFlowTrendBarChart = ({
  transactionTrendsData,
  mode,
}: CashFlowTrendBarChartProps) => {
  const { t } = useLocalization();
  const graphColors = useGraphColors();

  const chartData = useMemo(
    () =>
      buildTrendBarChartData(transactionTrendsData, mode, {
        income: t('income'),
        spending: t('spending'),
        other: (count) => `${t('legend.other')} (${String(count)})`,
      }),
    [transactionTrendsData, mode, t],
  );

  if (!chartData.dataset.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
        }}
      >
        <Typography>
          {t('dataFeeds.noDataAvailable', 'No data available')}
        </Typography>
      </Box>
    );
  }

  return (
    <BarChart
      height={BAR_CHART_HEIGHT}
      dataset={chartData.dataset}
      series={chartData.series.map((item, index) => ({
        ...item,
        color: graphColors[index % graphColors.length],
        valueFormatter: (value: number | null) =>
          formatNumberToCurrency(value ?? 0),
      }))}
      xAxis={[
        {
          height: 50,
          scaleType: 'band',
          dataKey: 'date',
          valueFormatter: (value: Date | null, ctx) =>
            formatChartDateAxis(value, ctx),
          tickLabelInterval: 'auto',
        },
      ]}
      yAxis={[
        {
          valueFormatter: (value: number) =>
            getCurrencyStringTitleForChart(value),
        },
      ]}
      sx={{
        width: '100%',
        [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
          transform: 'translateX(-10px)',
        },
        '& .MuiBarElement-root': {
          maxWidth: '120px',
        },
      }}
      slotProps={{
        legend: {
          direction: 'horizontal',
          position: { vertical: 'bottom', horizontal: 'center' },
        },
      }}
      borderRadius={8}
    />
  );
};

export default CashFlowTrendBarChart;
